import { useState } from "react";
import { toast } from "sonner";
import { Download, RotateCcw, Loader2 } from "lucide-react";
import { Field, Select, PrimaryButton, GhostButton, FileDrop, TextInput } from "@/tools/ui";
import { downloadDataUrl, formatBytes } from "@/lib/download";
import { API } from "@/lib/api";
import axios from "axios";

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

const dataUrlSize = (dataUrl) => {
  const base = dataUrl.split(",")[1] || "";
  return Math.floor((base.length * 3) / 4);
};

const Preview = ({ src, testid }) =>
  src ? (
    <div className="mt-6 border border-border bg-muted p-4">
      <img data-testid={testid} src={src} alt="result preview" className="mx-auto max-h-80 object-contain" />
    </div>
  ) : null;

export const ImageCompressor = () => {
  const [file, setFile] = useState(null);
  const [quality, setQuality] = useState(60);
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!file) return;
    const img = await loadImage(file);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", quality / 100);
    setOut({ dataUrl, size: dataUrlSize(dataUrl) });
    toast.success("Image compressed");
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="compress-drop" accept="image/*" label="Drop an image or click to upload" onFiles={(f) => { setFile(f[0]); setOut(null); }} />
      ) : (
        <>
          <div className="flex items-center justify-between border border-border bg-muted px-4 py-3">
            <span className="text-sm font-medium truncate">{file.name} · {formatBytes(file.size)}</span>
            <GhostButton data-testid="compress-reset" onClick={() => { setFile(null); setOut(null); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
          </div>
          <Field label={`Quality: ${quality}%`}>
            <input data-testid="compress-quality" type="range" min="10" max="100" value={quality} onChange={(e) => setQuality(+e.target.value)} className="w-full accent-primary" />
          </Field>
          <PrimaryButton data-testid="compress-run" onClick={run}>Compress Image</PrimaryButton>
          {out && (
            <div className="space-y-4">
              <p className="text-sm">New size: <b>{formatBytes(out.size)}</b> · Saved <b className="text-primary">{Math.max(0, Math.round((1 - out.size / file.size) * 100))}%</b></p>
              <Preview src={out.dataUrl} testid="compress-preview" />
              <PrimaryButton data-testid="compress-download" onClick={() => downloadDataUrl(out.dataUrl, "compressed.jpg")}><Download className="h-4 w-4" /> Download</PrimaryButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ImageResizer = () => {
  const [file, setFile] = useState(null);
  const [w, setW] = useState("");
  const [h, setH] = useState("");
  const [lock, setLock] = useState(true);
  const [ratio, setRatio] = useState(1);
  const [out, setOut] = useState(null);

  const onFile = async (f) => {
    const img = await loadImage(f);
    setFile(f); setOut(null);
    setW(img.naturalWidth); setH(img.naturalHeight);
    setRatio(img.naturalWidth / img.naturalHeight);
  };

  const run = async () => {
    if (!file) return;
    const img = await loadImage(file);
    const canvas = document.createElement("canvas");
    canvas.width = +w; canvas.height = +h;
    canvas.getContext("2d").drawImage(img, 0, 0, +w, +h);
    setOut(canvas.toDataURL("image/png"));
    toast.success("Image resized");
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="resize-drop" accept="image/*" label="Drop an image or click to upload" onFiles={(f) => onFile(f[0])} />
      ) : (
        <>
          <div className="flex items-center justify-between border border-border bg-muted px-4 py-3">
            <span className="text-sm font-medium truncate">{file.name}</span>
            <GhostButton data-testid="resize-reset" onClick={() => { setFile(null); setOut(null); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Width (px)"><TextInput data-testid="resize-w" type="number" value={w} onChange={(e) => { setW(e.target.value); if (lock) setH(Math.round(e.target.value / ratio)); }} /></Field>
            <Field label="Height (px)"><TextInput data-testid="resize-h" type="number" value={h} onChange={(e) => { setH(e.target.value); if (lock) setW(Math.round(e.target.value * ratio)); }} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="resize-lock" type="checkbox" checked={lock} onChange={(e) => setLock(e.target.checked)} className="accent-primary" /> Lock aspect ratio
          </label>
          <PrimaryButton data-testid="resize-run" onClick={run}>Resize Image</PrimaryButton>
          {out && (
            <div className="space-y-4">
              <Preview src={out} testid="resize-preview" />
              <PrimaryButton data-testid="resize-download" onClick={() => downloadDataUrl(out, "resized.png")}><Download className="h-4 w-4" /> Download</PrimaryButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ImageConverter = () => {
  const [file, setFile] = useState(null);
  const [fmt, setFmt] = useState("image/png");
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!file) return;
    const img = await loadImage(file);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (fmt === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
    ctx.drawImage(img, 0, 0);
    setOut(canvas.toDataURL(fmt, 0.92));
    toast.success("Image converted");
  };

  const ext = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" }[fmt];

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="convert-drop" accept="image/*" label="Drop an image or click to upload" onFiles={(f) => { setFile(f[0]); setOut(null); }} />
      ) : (
        <>
          <div className="flex items-center justify-between border border-border bg-muted px-4 py-3">
            <span className="text-sm font-medium truncate">{file.name}</span>
            <GhostButton data-testid="convert-reset" onClick={() => { setFile(null); setOut(null); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
          </div>
          <Field label="Convert to">
            <Select data-testid="convert-format" value={fmt} onChange={(e) => setFmt(e.target.value)}>
              <option value="image/png">PNG</option>
              <option value="image/jpeg">JPG</option>
              <option value="image/webp">WEBP</option>
            </Select>
          </Field>
          <PrimaryButton data-testid="convert-run" onClick={run}>Convert Image</PrimaryButton>
          {out && (
            <div className="space-y-4">
              <Preview src={out} testid="convert-preview" />
              <PrimaryButton data-testid="convert-download" onClick={() => downloadDataUrl(out, `converted.${ext}`)}><Download className="h-4 w-4" /> Download .{ext}</PrimaryButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ImageCropper = () => {
  const [file, setFile] = useState(null);
  const [aspect, setAspect] = useState("1:1");
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!file) return;
    const img = await loadImage(file);
    const [aw, ah] = aspect.split(":").map(Number);
    const target = aw / ah;
    const src = img.naturalWidth / img.naturalHeight;
    let sw = img.naturalWidth, sh = img.naturalHeight, sx = 0, sy = 0;
    if (src > target) { sw = img.naturalHeight * target; sx = (img.naturalWidth - sw) / 2; }
    else { sh = img.naturalWidth / target; sy = (img.naturalHeight - sh) / 2; }
    const canvas = document.createElement("canvas");
    canvas.width = sw; canvas.height = sh;
    canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    setOut(canvas.toDataURL("image/png"));
    toast.success("Image cropped");
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="crop-drop" accept="image/*" label="Drop an image or click to upload" onFiles={(f) => { setFile(f[0]); setOut(null); }} />
      ) : (
        <>
          <div className="flex items-center justify-between border border-border bg-muted px-4 py-3">
            <span className="text-sm font-medium truncate">{file.name}</span>
            <GhostButton data-testid="crop-reset" onClick={() => { setFile(null); setOut(null); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
          </div>
          <Field label="Aspect ratio (center crop)">
            <Select data-testid="crop-aspect" value={aspect} onChange={(e) => setAspect(e.target.value)}>
              <option value="1:1">Square 1:1</option>
              <option value="4:3">Standard 4:3</option>
              <option value="16:9">Widescreen 16:9</option>
              <option value="3:4">Portrait 3:4</option>
              <option value="9:16">Story 9:16</option>
            </Select>
          </Field>
          <PrimaryButton data-testid="crop-run" onClick={run}>Crop Image</PrimaryButton>
          {out && (
            <div className="space-y-4">
              <Preview src={out} testid="crop-preview" />
              <PrimaryButton data-testid="crop-download" onClick={() => downloadDataUrl(out, "cropped.png")}><Download className="h-4 w-4" /> Download</PrimaryButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const BackgroundRemover = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (f) => {
    setFile(f);
    setOut("");
    setPreview(URL.createObjectURL(f));
  };

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setOut("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/tools/remove-bg`, fd, { timeout: 120000 });
      setOut(`data:image/png;base64,${res.data.image_base64}`);
      toast.success("Background removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Removal failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="rmbg-drop" accept="image/*" label="Drop a photo or click to upload" onFiles={(f) => onFile(f[0])} />
      ) : (
        <>
          <div className="flex items-center justify-between border border-border bg-muted px-4 py-3">
            <span className="text-sm font-medium truncate">{file.name}</span>
            <GhostButton data-testid="rmbg-reset" onClick={() => { setFile(null); setOut(""); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
          </div>
          <PrimaryButton data-testid="rmbg-run" onClick={run} disabled={busy}>
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Removing…</> : "Remove Background"}
          </PrimaryButton>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="border border-border bg-muted p-4">
              <p className="overline text-muted-foreground">Original</p>
              <img src={preview} alt="original" className="mx-auto mt-2 max-h-72 object-contain" />
            </div>
            <div
              className="border border-border p-4"
              style={{ backgroundImage: "linear-gradient(45deg,#e5e5e5 25%,transparent 25%,transparent 75%,#e5e5e5 75%),linear-gradient(45deg,#e5e5e5 25%,transparent 25%,transparent 75%,#e5e5e5 75%)", backgroundSize: "20px 20px", backgroundPosition: "0 0,10px 10px" }}
            >
              <p className="overline text-muted-foreground">Result</p>
              {out ? (
                <img data-testid="rmbg-output" src={out} alt="no background" className="mx-auto mt-2 max-h-72 object-contain" />
              ) : (
                <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">{busy ? "Processing…" : "Result appears here"}</div>
              )}
            </div>
          </div>
          {out && <PrimaryButton data-testid="rmbg-download" onClick={() => downloadDataUrl(out, "no-background.png")}><Download className="h-4 w-4" /> Download PNG</PrimaryButton>}
        </>
      )}
    </div>
  );
};
